/**
 * useContentHistory - Content back/forward navigation for BufferSyncContext
 *
 * Extracted from BufferSyncContext.tsx. Manages the content history stack
 * for back/forward navigation between buffers.
 *
 * @module @humanizer/studio/hooks/useContentHistory
 */

import { useCallback, type Dispatch, type SetStateAction, type MutableRefObject } from 'react';
import type { ContentItem, ContentHistoryEntry } from '../contexts/buffer-sync-types';
import { createLogger } from '../utils/debug';

const log = createLogger('ContentHistory');

export interface ContentHistoryDeps {
  isElectron: boolean;
  contentHistoryRef: MutableRefObject<ContentHistoryEntry[]>;
  contentHistoryIndex: number;
  setContentHistoryIndex: Dispatch<SetStateAction<number>>;
  isRestoringHistory: MutableRefObject<boolean>;
  activeBufferIdRef: MutableRefObject<string | null>;
  workingContent: ContentItem[];
  setContent: (content: ContentItem[]) => Promise<void>;
  setSyncError: Dispatch<SetStateAction<string | null>>;
}

export interface ContentHistoryReturn {
  loadHistoryEntry: (entry: ContentHistoryEntry) => Promise<ContentItem[] | null>;
  contentGoBack: () => void;
  contentGoForward: () => void;
  pushContentHistory: (label: string) => void;
  canGoBack: boolean;
  canGoForward: boolean;
  contentHistoryLabel: string | null;
}

export function useContentHistory(deps: ContentHistoryDeps): ContentHistoryReturn {
  const {
    isElectron,
    contentHistoryRef,
    contentHistoryIndex,
    setContentHistoryIndex,
    isRestoringHistory,
    activeBufferIdRef,
    workingContent,
    setContent,
    setSyncError,
  } = deps;

  // Helper: load content from a history entry (handles both buffer ID and content copy)
  const loadHistoryEntry = useCallback(async (entry: ContentHistoryEntry): Promise<ContentItem[] | null> => {
    if (entry.bufferId && isElectron && window.electronBuffer) {
      try {
        const { bufferTextToContentItems } = await import('../utils/buffer-bridge');
        const buffer = await window.electronBuffer.load(entry.bufferId);
        if (buffer) {
          activeBufferIdRef.current = buffer.id;
          return bufferTextToContentItems(buffer.text, buffer.origin);
        }
        log.warn('History buffer not found:', entry.bufferId);
        setSyncError('History entry not found — buffer may have been deleted');
      } catch (err) {
        log.warn('Failed to load history buffer:', err);
        setSyncError('Failed to load history entry');
      }
      return null;
    }
    return entry.content ?? null;
  }, [isElectron, activeBufferIdRef, setSyncError]);

  const pushContentHistory = (label: string) => {
    if (workingContent.length === 0) return;

    // Auto-checkpoint: commit current buffer before navigating away
    if (isElectron && activeBufferIdRef.current) {
      window.electronBuffer?.commit(activeBufferIdRef.current).catch(() => {});
    }

    const history = contentHistoryRef.current;
    // Truncate any forward entries beyond current index
    if (contentHistoryIndex >= 0 && contentHistoryIndex < history.length - 1) {
      contentHistoryRef.current = history.slice(0, contentHistoryIndex + 1);
    }

    // Electron: store buffer ID (persisted, lazy-loaded). Web: store content copy.
    if (isElectron && activeBufferIdRef.current) {
      contentHistoryRef.current.push({ bufferId: activeBufferIdRef.current, label });
      // Null out so the next setContent creates a NEW buffer instead of overwriting
      activeBufferIdRef.current = null;
    } else {
      contentHistoryRef.current.push({ content: workingContent, label });
    }

    // Cap at 20 entries
    if (contentHistoryRef.current.length > 20) {
      contentHistoryRef.current = contentHistoryRef.current.slice(-20);
    }

    setContentHistoryIndex(contentHistoryRef.current.length - 1);

    // Persist history stack to electron-store (fire-and-forget)
    if (isElectron && window.electronAPI?.store) {
      const persistable = contentHistoryRef.current
        .filter(e => e.bufferId)
        .map(e => ({ bufferId: e.bufferId, label: e.label }));
      window.electronAPI.store.set('contentHistoryStack', persistable).catch(() => {});
      window.electronAPI.store.set('contentHistoryIndex', contentHistoryRef.current.length - 1).catch(() => {});
    }
  };

  const contentGoBack = useCallback(() => {
    const history = contentHistoryRef.current;
    if (contentHistoryIndex < 0 || history.length === 0) return;

    const oldIndex = contentHistoryIndex;
    let targetIndex: number;

    // If at the end, push current content so we can go forward to it
    if (contentHistoryIndex === history.length - 1 && workingContent.length > 0) {
      const currentLabel = history[contentHistoryIndex]?.label ?? 'Current';
      const lastEntry = history[history.length - 1];
      const isCurrentAlreadySaved = isElectron
        ? (lastEntry?.bufferId === activeBufferIdRef.current)
        : (lastEntry?.content === workingContent);
      if (!lastEntry || !isCurrentAlreadySaved) {
        if (isElectron && activeBufferIdRef.current) {
          contentHistoryRef.current.push({ bufferId: activeBufferIdRef.current, label: currentLabel });
        } else {
          contentHistoryRef.current.push({ content: workingContent, label: currentLabel });
        }
        // After push, navigate to what was previously the last entry
        targetIndex = contentHistoryRef.current.length - 2;
      } else {
        targetIndex = contentHistoryIndex - 1;
      }
    } else if (contentHistoryIndex > 0) {
      targetIndex = contentHistoryIndex - 1;
    } else {
      return; // Already at beginning
    }

    if (targetIndex < 0) return;

    const entry = history[targetIndex];
    if (!entry) return;

    setContentHistoryIndex(targetIndex);
    isRestoringHistory.current = true;
    loadHistoryEntry(entry).then(items => {
      if (items) {
        setContent(items).finally(() => {
          isRestoringHistory.current = false;
        });
      } else {
        // Buffer deleted/unavailable — revert index, keep current content
        log.warn('contentGoBack: entry unavailable, reverting index');
        setContentHistoryIndex(oldIndex);
        isRestoringHistory.current = false;
      }
    }).catch(() => {
      setContentHistoryIndex(oldIndex);
      isRestoringHistory.current = false;
    });
  }, [contentHistoryIndex, workingContent, setContent, loadHistoryEntry, isElectron, contentHistoryRef, activeBufferIdRef, isRestoringHistory, setContentHistoryIndex]);

  const contentGoForward = useCallback(() => {
    const history = contentHistoryRef.current;
    if (contentHistoryIndex >= history.length - 1) return;

    const prevIndex = contentHistoryIndex;
    const nextIndex = contentHistoryIndex + 1;
    setContentHistoryIndex(nextIndex);

    const entry = history[nextIndex];
    if (entry) {
      isRestoringHistory.current = true;
      loadHistoryEntry(entry).then(items => {
        if (items) {
          setContent(items).finally(() => {
            isRestoringHistory.current = false;
          });
        } else {
          // Buffer deleted/unavailable — revert index, keep current content
          log.warn('contentGoForward: entry unavailable, reverting index');
          setContentHistoryIndex(prevIndex);
          isRestoringHistory.current = false;
        }
      }).catch(() => {
        setContentHistoryIndex(prevIndex);
        isRestoringHistory.current = false;
      });
    }
  }, [contentHistoryIndex, setContent, loadHistoryEntry, contentHistoryRef, isRestoringHistory, setContentHistoryIndex]);

  const canGoBack = contentHistoryIndex > 0;
  const canGoForward = contentHistoryIndex < contentHistoryRef.current.length - 1;
  const contentHistoryLabel = contentHistoryRef.current[contentHistoryIndex]?.label ?? null;

  return {
    loadHistoryEntry,
    contentGoBack,
    contentGoForward,
    pushContentHistory,
    canGoBack,
    canGoForward,
    contentHistoryLabel,
  };
}
